import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bookingsAPI, locationsAPI } from '../../api/bookings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Download, X, CreditCard, Clock, MapPin, QrCode, Receipt } from 'lucide-react';
import CustomerCancelBookingDialog from '../../components/customer/CustomerCancelBookingDialog';
import { formatDate, getStatusColor, getSportColor } from '../../lib/utils';
import { formatBookingReference, formatPaymentMethod } from '../../utils/bookingUtils';
import DateTimeDisplay from '../../components/booking/DateTimeDisplay';

// Status-aware price resolver: only PENDING_ALLOCATION has a default
// final_total = 0.0 (admin hasn't allocated courts yet), so we fall back
// to estimated_total in that case. Once courts are assigned (AWAITING_PAYMENT
// onwards), final_total is populated and already reflects any discount.
// Using final_total here keeps Gross − Discount = Net consistent.
const PRE_PAYMENT_STATUSES = new Set(['PENDING_ALLOCATION']);
const getDisplayTotal = (booking) => {
  if (!booking) return 0;
  if (PRE_PAYMENT_STATUSES.has(booking.status)) {
    return booking.estimated_total ?? booking.final_total ?? 0;
  }
  return booking.final_total ?? booking.estimated_total ?? 0;
};
const getDisplayGross = (booking) => {
  if (!booking) return 0;
  if (PRE_PAYMENT_STATUSES.has(booking.status)) {
    return booking.gross_total ?? booking.estimated_total ?? booking.final_total ?? 0;
  }
  return booking.gross_total ?? booking.final_total ?? booking.estimated_total ?? 0;
};

export default function BookingDetails() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const loadBooking = async () => {
    try {
      const data = await bookingsAPI.getBooking(bookingId);
      setBooking(data);
      
      // Load location info
      if (data.location_id) {
        try {
          const locationData = await locationsAPI.getLocation(data.location_id);
          setLocation(locationData);
        } catch (locError) {
          console.error('Failed to load location:', locError);
        }
      }
    } catch (error) {
      toast.error('Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => setCancelDialogOpen(true);

  const handleCancelConfirm = async (reason) => {
    setCancelling(true);
    try {
      await bookingsAPI.cancelBooking(bookingId, { reason });
      toast.success('Booking cancelled. A confirmation email is on its way.');
      setCancelDialogOpen(false);
      await loadBooking();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to cancel booking';
      toast.error(errorMsg);
    } finally {
      setCancelling(false);
    }
  };

  const handlePayment = () => {
    navigate(`/payment/${bookingId}`);
  };

  const handleCheckPaymentStatus = async () => {
    setCheckingPayment(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(
        `${API_URL}/api/bookings/${bookingId}/payment/check-gateway-status`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      // IMPORTANT: Check response.ok BEFORE parsing JSON
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || data.message || 'Failed to check payment status');
      }

      // Now safe to parse the response
      const data = await response.json();

      if (data.status === 'confirmed') {
        toast.success('Payment confirmed! Refreshing booking...');
        await loadBooking();
      } else if (data.status === 'failed') {
        toast.error(data.message || 'Payment failed or timeout');
      } else if (data.status === 'pending') {
        toast.info(data.message || 'Payment still pending');
      } else if (data.status === 'error') {
        toast.error(data.message || 'Failed to check payment status');
      }
    } catch (error) {
      console.error('Payment check error:', error);
      toast.error(error.message || 'Failed to check payment status');
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleDownloadCalendar = async () => {
    try {
      const blob = await bookingsAPI.downloadCalendar(bookingId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booking-${bookingId}.ics`;
      a.click();
      toast.success('Calendar file downloaded');
    } catch (error) {
      toast.error('Failed to download calendar');
    }
  };

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Booking not found</p>
      </div>
    );
  }

  return (
    <div className="mobile-container min-h-screen pb-6" data-testid="booking-details">
      <div className="bg-primary text-primary-foreground p-6">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/bookings')}
            data-testid="back-button"
            className="text-primary-foreground hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-black font-heading uppercase">Booking Details</h1>
        </div>
        <span className={`status-badge ${getStatusColor(booking.status)}`}>
          {booking.status.replace('_', ' ')}
        </span>
      </div>

      <div className="p-6 space-y-4">
        {/* Booking Info */}
        <Card className="p-6 glass-card">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground">Booking Reference</p>
                <p className="font-mono text-xl font-bold">{formatBookingReference(booking.id)}</p>
              </div>
              {booking.payment_method && booking.status === 'CONFIRMED' && (
                <div className="text-right">
                  <p className="text-xs uppercase font-bold text-muted-foreground">Payment Method</p>
                  <div className="flex items-center justify-end gap-2 mt-1">
                    {/* Icon picks up the actual mode: QR / Biller / Card */}
                    {(() => {
                      const pm = (booking.payment_method || '').toLowerCase();
                      if (pm === 'pocket_pay_qr') return <QrCode className="w-4 h-4 text-green-600" />;
                      if (pm === 'pocket_pay_biller') return <Receipt className="w-4 h-4 text-purple-600" />;
                      return <CreditCard className="w-4 h-4 text-blue-600" />;
                    })()}
                    <p className={`text-lg font-semibold ${(() => {
                      const pm = (booking.payment_method || '').toLowerCase();
                      if (pm === 'pocket_pay_qr') return 'text-green-600';
                      if (pm === 'pocket_pay_biller') return 'text-purple-600';
                      return 'text-blue-600';
                    })()}`}>
                      {formatPaymentMethod(booking.payment_method)}
                    </p>
                  </div>
                  {/* Pocket Pay transaction reference — grep-able for support
                      tickets and gateway dashboard lookups. Only shown when
                      the booking actually has a Pocket Pay order id. */}
                  {booking.pocket_pay_order_id && (
                    <p className="text-[10px] font-mono text-muted-foreground mt-1"
                       title="Pocket Pay transaction reference"
                       data-testid="pocketpay-txn-ref">
                      Ref: {booking.pocket_pay_order_id}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Location */}
            {location && (
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-xl">
                <p className="text-xs uppercase font-bold text-muted-foreground mb-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  Location
                </p>
                <p className="font-bold text-lg">{location.name}</p>
                <p className="text-sm text-muted-foreground">{location.address}</p>
                {location.city && (
                  <p className="text-sm text-muted-foreground">{location.city}</p>
                )}
                {location.phone && (
                  <p className="text-sm text-muted-foreground mt-1">📞 {location.phone}</p>
                )}
              </div>
            )}
            
            <div>
              <p className="text-xs uppercase font-bold text-muted-foreground">Date & Time</p>
              <DateTimeDisplay
                date={booking.booking_date}
                timeSlot={booking.time_slot}
                duration={booking.duration}
                className="mt-1"
                testId="booking-details"
              />
              {booking.duration && booking.duration > 1 && (
                <span className="inline-block mt-2 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-bold">
                  {booking.duration} hours
                </span>
              )}
            </div>
            <div>
              <p className="text-xs uppercase font-bold text-muted-foreground">Requested Sport(s)</p>
              <div className="flex gap-2 mt-2">
                {booking.courts_requested.map((court, idx) => (
                  <span key={`${court.sport}-${idx}`} className={`sport-badge ${getSportColor(court.sport)}`}>
                    {court.sport} x{court.quantity}
                  </span>
                ))}
              </div>
            </div>
            {booking.courts_assigned && booking.courts_assigned.length > 0 && (
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground">Assigned Court(s)</p>
                <div className="flex gap-2 mt-2">
                  {booking.courts_assigned.map((court) => (
                    <span key={`${court.court_number}-${court.sport}`} className={`sport-badge ${getSportColor(court.sport)}`}>
                      Court {court.court_number} - {court.sport}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {booking.extras && booking.extras.length > 0 && (
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground">Extras</p>
                {booking.extras.map((extra, idx) => (
                  <p key={`${extra.equipment_name}-${idx}`} className="text-sm">
                    {extra.equipment_name} x{extra.quantity} - ${(extra.price * extra.quantity).toFixed(2)}
                  </p>
                ))}
              </div>
            )}
            {booking.special_requests && (
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground">Special Requests</p>
                <p className="text-sm">{booking.special_requests}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Total */}
        <Card className="p-6 glass-card border-accent">
          {/* Show breakdown only if there's an actual discount (gross != final) */}
          {(booking.discount_value > 0 || 
           (booking.gross_total && booking.final_total && booking.gross_total > booking.final_total)) ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium text-muted-foreground">Gross Amount</p>
                <p className="text-xl font-bold">${getDisplayGross(booking).toFixed(2)}</p>
              </div>
              
              {/* Show discount if there's an actual discount amount */}
              {(booking.discount_value > 0 || (booking.gross_total && booking.final_total && booking.gross_total > booking.final_total)) && (
                <>
                  <div className="flex justify-between items-center text-red-600">
                    <div>
                      <p className="text-sm font-medium">
                        {booking.discount_value > 0 ? (
                          `Discount (${booking.discount_type === 'percentage' ? `${booking.discount_value}%` : `$${booking.discount_value}`})`
                        ) : (
                          // Guard division: gross_total could be 0 for comp/block rows.
                          `Discount (${booking.gross_total
                            ? (((booking.gross_total - (booking.final_total ?? 0)) / booking.gross_total) * 100).toFixed(0)
                            : 0}%)`
                        )}
                      </p>
                      {booking.discount_reason && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {booking.discount_reason}
                        </p>
                      )}
                    </div>
                    <p className="text-xl font-bold">-${(getDisplayGross(booking) - (booking.final_total ?? 0)).toFixed(2)}</p>
                  </div>
                  <div className="border-t pt-3"></div>
                </>
              )}
              
              <div className="flex justify-between items-center">
                <p className="text-lg font-bold uppercase">Net Amount</p>
                <p className="text-3xl font-black font-heading text-primary">${getDisplayTotal(booking).toFixed(2)}</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <p className="text-lg font-bold uppercase">
                {booking.status === 'PENDING_ALLOCATION' ? 'Estimated Total' : 'Total'}
              </p>
              <p className="text-3xl font-black font-heading">
                ${getDisplayTotal(booking).toFixed(2)}
              </p>
            </div>
          )}
        </Card>

        {/* Actions */}
        <div className="space-y-3">
          {booking.status === 'AWAITING_PAYMENT' && (
            <>
              <Button
                onClick={handlePayment}
                data-testid="pay-button"
                className="w-full btn-volt h-14"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Confirm & Pay
              </Button>
              
              {booking.pocket_pay_order_id && (
                <Button
                  onClick={handleCheckPaymentStatus}
                  data-testid="check-payment-button"
                  variant="outline"
                  className="w-full h-14 border-2 border-primary text-primary hover:bg-primary hover:text-white"
                  disabled={checkingPayment}
                >
                  <Clock className="w-5 h-5 mr-2" />
                  {checkingPayment ? 'Checking...' : 'Check Payment Status'}
                </Button>
              )}
            </>
          )}

          {booking.status === 'CONFIRMED' && (
            <Button
              onClick={handleDownloadCalendar}
              data-testid="download-calendar-button"
              className="w-full btn-primary h-14"
            >
              <Download className="w-5 h-5 mr-2" />
              Add to Calendar
            </Button>
          )}

          {booking.status !== 'CANCELLED' && booking.status !== 'CONFIRMED' && (
            <Button
              variant="destructive"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCancelClick();
              }}
              data-testid="cancel-booking-button"
              className="w-full h-14 rounded-full"
              disabled={cancelling}
              style={{ pointerEvents: 'auto', cursor: cancelling ? 'not-allowed' : 'pointer' }}
            >
              <X className="w-5 h-5 mr-2" />
              {cancelling ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          )}
          
          {booking.status === 'CANCELLED' && (
            <div className="p-4 bg-destructive/10 text-destructive rounded-2xl text-center" data-testid="cancelled-notice">
              <p className="font-bold">This booking has been cancelled</p>
            </div>
          )}

          {booking.status === 'RELEASED' && (
            <div className="p-4 bg-sky-50 text-sky-800 rounded-2xl text-center" data-testid="released-notice">
              <p className="font-bold">This booking was released by SmashZone team</p>
              <p className="text-sm mt-1">Your payment remains on file. We'll contact you to arrange a replacement, or reach out to us directly.</p>
            </div>
          )}
        </div>
      </div>

      <CustomerCancelBookingDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        bookingRef={booking ? `#${booking.id.slice(0, 8)}` : ''}
        submitting={cancelling}
        onConfirm={handleCancelConfirm}
      />
    </div>
  );
}